import { Injectable } from '@angular/core';

import { ParticipantMarketStatus, ParticipantMarketType, ParticipantPriceType } from '@cds/betting-offer';
import { MarketView, RaceMarketViewModel, WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { orderBy, uniqBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { HorsesFixtureTranslationHelper } from './horses-fixture-translation-helper.service';

@Injectable({
    providedIn: 'root',
})
export class HorsesFixtureMarketHelper {
    private event = new BehaviorSubject<string>('');
    scrollEvent = this.event.asObservable();

    constructor(private horsesFixtureTranslationHelper: HorsesFixtureTranslationHelper) {}

    isWinMarketFixedOddsLocked(fixture: WinGameFixture): boolean {
        return this.getParticipants(fixture.participants, false);
    }

    private getParticipants(participants: WinGameParticipant[], areWinMarketsSuspended: boolean): boolean {
        return participants
            .flatMap((p) => p.options)
            .filter((o) => {
                if (areWinMarketsSuspended) {
                    return o.marketType === ParticipantMarketType.Win;
                } else {
                    return o.marketType === ParticipantMarketType.Win && o.priceType === ParticipantPriceType.Fixed;
                }
            })
            .every((o) => {
                if (areWinMarketsSuspended) {
                    return o.marketStatus === ParticipantMarketStatus.Suspended;
                } else {
                    return o.marketStatus !== ParticipantMarketStatus.Visible;
                }
            });
    }

    getMarkets(fixture: WinGameFixture): RaceMarketViewModel[] {
        return this.getMarketData(fixture, false) as RaceMarketViewModel[];
    }

    getMarketsTabs(fixture: WinGameFixture): TabBarItem[] {
        return this.getMarketData(fixture, true) as TabBarItem[];
    }

    private getMarketData(fixture: WinGameFixture, isTabs: boolean): (RaceMarketViewModel | TabBarItem)[] {
        const marketConfig: { [key: string]: { view: MarketView; order: number } } = {
            [ParticipantMarketType.Win]: {
                view: MarketView.Win,
                order: 1,
            },
            [ParticipantMarketType.Forecast]: {
                view: MarketView.Forecast,
                order: 2,
            },
            [ParticipantMarketType.Tricast]: {
                view: MarketView.Tricast,
                order: 3,
            },
        };

        let options = fixture.participants.flatMap((o) => o.options).filter((o) => !!marketConfig[o.marketType]);
        options = uniqBy(options, (a) => a.marketType);
        options = orderBy(options, (o) => marketConfig[o.marketType].order);

        const markets: (TabBarItem | RaceMarketViewModel)[] = !isTabs
            ? options.map((m) => ({
                  view: marketConfig[m.marketType].view,
                  name: this.horsesFixtureTranslationHelper.translateMarketView(marketConfig[m.marketType].view),
              }))
            : options.map((market, index) => ({
                  id: market.id,
                  active: index === 0,
                  title: this.horsesFixtureTranslationHelper.translateMarketView(marketConfig[market.marketType].view),
                  attribute: marketConfig[market.marketType].view,
              }));

        if (!isTabs && fixture.optionMarkets.length > 0) {
            (markets as RaceMarketViewModel[]).push({
                view: MarketView.Specials,
                name: this.horsesFixtureTranslationHelper.translateMarketView(MarketView.Specials),
            } as RaceMarketViewModel);
        }

        return markets;
    }

    getDerivedMarkets(fixture: WinGameFixture): TabBarItem[] {
        if (!fixture || !fixture.optionSets) {
            return [];
        }

        const marketTabs: TabBarItem[] = fixture.optionSets.map((optionSet, index) => ({
            id: optionSet.id,
            title: optionSet.name,
            active: index === 0,
            marketTabOrder: optionSet.id,
        }));

        return marketTabs;
    }

    checkRacingOptionMarketTypes(racingOptionMarketType: string, marketType?: string): boolean {
        return marketType === racingOptionMarketType;
    }
}
